import React, { ReactNode, useEffect, useReducer } from 'react';
import {
  Button, ButtonToolbar, Form, IconButton, Loader, SelectPicker, Stack,
} from 'rsuite';
import EditIcon from '@rsuite/icons/Edit';
import TrashIcon from '@rsuite/icons/Trash';
import axios from 'axios';
import _ from 'lodash';
import { Eshop, getPartnerEshopDefaults, savePartnerEshopDefaults } from '../../../api/eshop/eshop';
import { AllegroShippingRate } from '../../../api/allegroApiTypes';
import { PartnerEshopDefaults as Defaults } from '../../../api/apiTypes';
import { getAllegroShippingRates } from '../../../api/allegro';
import { AllegroReturnPolicy } from '../../../api/allegro/returnpolicy/returnPolicyTypes';
import { deleteAllegroReturnPolicy } from '../../../api/allegro/returnpolicy/returnPolicy';
import { AllegroImpliedWarranty } from '../../../api/allegro/impliedwarranty/impliedWarrantyTypes';
import fetchAllegroReturnPolicies from '../../../services/allegro/returnpolicy/fetchAllegroReturnPolicies';
import downloadReturnPoliciesExcel from '../../../services/allegro/returnpolicy/downloadReturnPoliciesExcel';
import uploadReturnPoliciesExcel from '../../../services/allegro/returnpolicy/uploadReturnPoliciesExcel';
import fetchAllegroImpliedWarranties from '../../../services/allegro/impliedwarranty/fetchAllegroImpliedWarranties';
import downloadImpliedWarrantiesExcel from '../../../services/allegro/impliedwarranty/downloadImpliedWarrantiesExcel';
import uploadImpliedWarrantiesExcel from '../../../services/allegro/impliedwarranty/uploadImpliedWarrantiesExcel';
import EshopSetupAllegroMappedDeliveryMethods from './EshopSetupAllegroMappedDeliveryMethods';
import EshopSetupAllegroReturnPolicyEditModal from './ReturnPolicy/EshopSetupAllegroReturnPolicyEditModal';
import EshopSetupAllegroImpliedWarrantyEditModal from './ImpliedWarranty/EshopSetupAllegroImpliedWarrantyEditModal';
import Alert from '../../Alert';
import { utils } from '../../../utils/utils';
import { showErrorMessage, showSuccessMessage } from '../../../utils/message';
import FilePicker from '../../FilePicker';
import { SelectOptionWithActions, SelectPickerWithActions } from '../../SelectPickerWithActions';

interface Props {
  eshop: Eshop;
}

interface ReturnPoliciesState {
  isEditing: boolean;
  editingReturnPolicy?: AllegroReturnPolicy;
  isDeleting: boolean;
  deletingReturnPolicy?: AllegroReturnPolicy;
}

interface ImpliedWarrantiesState {
  isEditing: boolean;
  editingImpliedWarranty?: AllegroImpliedWarranty;
}

interface State {
  isLoading: boolean;
  isSaving: boolean;
  data: Data;
  defaultsEdit: Defaults;
  returnPolicies: ReturnPoliciesState;
  impliedWarranties: ImpliedWarrantiesState;
}

interface Data {
  defaults?: Defaults;
  shippingRates: AllegroShippingRate[];
  returnPolicies: AllegroReturnPolicy[];
  impliedWarranties: AllegroImpliedWarranty[];
}

const initialState: State = {
  isLoading: false,
  isSaving: false,
  data: {
    defaults: undefined,
    shippingRates: [],
    returnPolicies: [],
    impliedWarranties: [],
  },
  defaultsEdit: {
    defaultShippingRateId: '',
    defaultReturnPolicyId: '',
    defaultWarrantyId: '',
  },
  returnPolicies: {
    isEditing: false,
    editingReturnPolicy: undefined,
    isDeleting: false,
    deletingReturnPolicy: undefined,
  },
  impliedWarranties: {
    isEditing: false,
    editingImpliedWarranty: undefined,
  },
};

type LoadingAction = { type: 'loading' };
type LoadingSuccessfulAction = { type: 'loadingSuccessful', payload: Data };
type EditAction = { type: 'edit', payload: Defaults };
type ResetAction = { type: 'reset' };
type SavingAction = { type: 'saving', payload: boolean };
type ReturnPoliciesEditOpenAction = {
  type: 'returnPolicies/edit/open',
  payload?: AllegroReturnPolicy
};
type ReturnPoliciesEditCloseAction = { type: 'returnPolicies/edit/close' };
type ReturnPoliciesDeleteOpenAction = {
  type: 'returnPolicies/delete/open',
  payload: AllegroReturnPolicy
};
type ReturnPoliciesDeleteCloseAction = { type: 'returnPolicies/delete/close' };
type ReturnPoliciesDeleteLoadingAction = {
  type: 'returnPolicies/delete/loading',
  payload: boolean
};
type ImpliedWarrantiesEditOpenAction = {
  type: 'impliedWarranties/edit/open',
  payload?: AllegroImpliedWarranty
};
type ImpliedWarrantiesEditCloseAction = { type: 'impliedWarranties/edit/close' };
type Action = LoadingAction
| LoadingSuccessfulAction
| EditAction
| ResetAction
| SavingAction
| ReturnPoliciesEditOpenAction
| ReturnPoliciesEditCloseAction
| ReturnPoliciesDeleteOpenAction
| ReturnPoliciesDeleteCloseAction
| ReturnPoliciesDeleteLoadingAction
| ImpliedWarrantiesEditOpenAction
| ImpliedWarrantiesEditCloseAction;

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'loading':
      return { ...state, isLoading: true };
    case 'loadingSuccessful':
      return {
        ...state,
        isLoading: false,
        data: action.payload,
        defaultsEdit: {
          defaultShippingRateId: _.defaultTo(action.payload.defaults?.defaultShippingRateId, ''),
          defaultReturnPolicyId: _.defaultTo(action.payload.defaults?.defaultReturnPolicyId, ''),
          defaultWarrantyId: _.defaultTo(action.payload.defaults?.defaultWarrantyId, ''),
        },
      };
    case 'edit':
      return { ...state, defaultsEdit: action.payload };
    case 'reset':
      return {
        ...state,
        defaultsEdit: {
          defaultShippingRateId: _.defaultTo(state.data.defaults?.defaultShippingRateId, ''),
          defaultReturnPolicyId: _.defaultTo(state.data.defaults?.defaultReturnPolicyId, ''),
          defaultWarrantyId: _.defaultTo(state.data.defaults?.defaultWarrantyId, ''),
        },
      };
    case 'saving':
      return { ...state, isSaving: action.payload };
    case 'returnPolicies/edit/open':
      return {
        ...state,
        returnPolicies: {
          ...state.returnPolicies,
          isEditing: true,
          editingReturnPolicy: action.payload,
        },
      };
    case 'returnPolicies/edit/close':
      return {
        ...state,
        returnPolicies: {
          ...state.returnPolicies,
          isEditing: false,
          editingReturnPolicy: undefined,
        },
      };
    case 'returnPolicies/delete/open':
      return {
        ...state,
        returnPolicies: {
          ...state.returnPolicies,
          deletingReturnPolicy: action.payload,
        },
      };
    case 'returnPolicies/delete/close':
      return {
        ...state,
        returnPolicies: {
          ...state.returnPolicies,
          deletingReturnPolicy: undefined,
        },
      };
    case 'returnPolicies/delete/loading':
      return {
        ...state,
        returnPolicies: {
          ...state.returnPolicies,
          isDeleting: action.payload,
        },
      };
    case 'impliedWarranties/edit/open':
      return {
        ...state,
        impliedWarranties: {
          ...state.impliedWarranties,
          isEditing: true,
          editingImpliedWarranty: action.payload,
        },
      };
    case 'impliedWarranties/edit/close':
      return {
        ...state,
        impliedWarranties: {
          ...state.impliedWarranties,
          isEditing: false,
          editingImpliedWarranty: undefined,
        },
      };
    default:
      return state;
  }
}

function renderItem(value: any, item?: any): React.ReactNode {
  return item ? (
    <p>
      <strong>{item.name}</strong>
      {' '}
      {`(${item.id})`}
    </p>
  ) : value;
}

export default function EshopSetupAllegroDefaults({ eshop }: Props) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { deletingReturnPolicy } = state.returnPolicies;

  async function fetchDefaults(): Promise<Defaults | undefined> {
    try {
      return await getPartnerEshopDefaults(eshop.code);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status !== 404) {
        throw error;
      }
      return undefined;
    }
  }

  async function fetchAllegro(): Promise<[
    AllegroShippingRate[],
    AllegroReturnPolicy[],
    AllegroImpliedWarranty[],
  ]> {
    try {
      return await Promise.all([
        getAllegroShippingRates(eshop.code),
        fetchAllegroReturnPolicies(eshop.code),
        fetchAllegroImpliedWarranties(eshop.code),
      ]);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status !== 401) {
        throw error;
      }
      return [[], [], []];
    }
  }

  async function fetchData(): Promise<void> {
    dispatch({ type: 'loading' });

    const [defaults, [shippingRates, returnPolicies, impliedWarranties]] = await Promise.all([
      fetchDefaults(),
      fetchAllegro(),
    ]);

    const response = {
      defaults,
      shippingRates,
      returnPolicies,
      impliedWarranties,
    };

    dispatch({ type: 'loadingSuccessful', payload: response });
  }

  async function saveDefaults(): Promise<void> {
    dispatch({ type: 'saving', payload: true });
    const {
      defaultShippingRateId,
      defaultReturnPolicyId,
      defaultWarrantyId,
    } = state.defaultsEdit;
    const defaults: Defaults = {
      defaultShippingRateId: utils.defaultToUndefined(defaultShippingRateId),
      defaultReturnPolicyId: utils.defaultToUndefined(defaultReturnPolicyId),
      defaultWarrantyId: utils.defaultToUndefined(defaultWarrantyId),
    };
    try {
      await savePartnerEshopDefaults(eshop.code, defaults);
    } finally {
      dispatch({ type: 'saving', payload: false });
    }
  }

  async function deleteReturnPolicy(returnPolicy: AllegroReturnPolicy): Promise<void> {
    dispatch({ type: 'returnPolicies/delete/loading', payload: true });

    try {
      await deleteAllegroReturnPolicy(eshop.code, returnPolicy.id);
      dispatch({ type: 'returnPolicies/delete/close' });
      showSuccessMessage(`Return terms ${returnPolicy.name} have been successfully deleted.`);
      await fetchData();
    } catch (error) {
      if (axios.isAxiosError(error)) {
        showErrorMessage(JSON.stringify(error.response?.data));
      }
    } finally {
      dispatch({ type: 'returnPolicies/delete/loading', payload: false });
    }
  }

  useEffect(() => {
    fetchData().then();
  }, [eshop]);

  async function onSave() {
    await saveDefaults();
    showSuccessMessage('Defaults successfully saved.');
  }

  function onCancel() {
    dispatch({ type: 'reset' });
  }

  function handleEditReturnPolicy(returnPolicy?: AllegroReturnPolicy) {
    dispatch({ type: 'returnPolicies/edit/open', payload: returnPolicy });
  }

  function handleDeleteReturnPolicy(returnPolicy: AllegroReturnPolicy) {
    dispatch({ type: 'returnPolicies/delete/open', payload: returnPolicy });
  }

  function handleDeleteReturnPolicyClose() {
    dispatch({ type: 'returnPolicies/delete/close' });
  }

  async function handleDeleteReturnPolicyConfirm(returnPolicy: AllegroReturnPolicy) {
    await deleteReturnPolicy(returnPolicy);
  }

  async function handleImportReturnPolicies(file: File) {
    await uploadReturnPoliciesExcel(eshop.code, file);
    await fetchData();
  }

  async function handleExportReturnPolicies() {
    await downloadReturnPoliciesExcel(state.data.returnPolicies);
  }

  function handleReturnPolicyClosed() {
    dispatch({ type: 'returnPolicies/edit/close' });
  }

  function handleReturnPolicySaved() {
    dispatch({ type: 'returnPolicies/edit/close' });
    fetchData().then();
  }

  function handleEditImpliedWarranty(impliedWarranty?: AllegroImpliedWarranty) {
    dispatch({ type: 'impliedWarranties/edit/open', payload: impliedWarranty });
  }

  async function handleImportImpliedWarranties(file: File) {
    await uploadImpliedWarrantiesExcel(eshop.code, file);
    await fetchData();
  }

  async function handleExportImpliedWarranties() {
    await downloadImpliedWarrantiesExcel(eshop.code, state.data.impliedWarranties);
  }

  function handleImpliedWarrantyClosed() {
    dispatch({ type: 'impliedWarranties/edit/close' });
  }

  function handleImpliedWarrantySaved() {
    dispatch({ type: 'impliedWarranties/edit/close' });
    fetchData().then();
  }

  return state.isLoading ? <Loader /> : (
    <Form
      layout="horizontal"
      formValue={state.defaultsEdit}
      onChange={(defaultsEdit) => dispatch({ type: 'edit', payload: defaultsEdit })}
    >
      <Form.Group>
        <Stack>
          <Form.ControlLabel>Shipping price list</Form.ControlLabel>
          <Form.Control
            name="defaultShippingRateId"
            accepter={SelectPicker}
            data={state.data.shippingRates}
            value={state.defaultsEdit.defaultShippingRateId ?? null}
            valueKey="id"
            labelKey="name"
            renderMenuItem={renderItem}
            renderValue={renderItem}
            cleanable
          />
          <div style={{ marginLeft: 20 }}>
            <EshopSetupAllegroMappedDeliveryMethods
              eshopCode={eshop.code}
              shippingRateId={state.defaultsEdit.defaultShippingRateId}
            />
          </div>
        </Stack>
      </Form.Group>
      <Form.Group>
        <Form.ControlLabel>Returns terms</Form.ControlLabel>
        <Stack spacing={20}>
          <Form.Control
            name="defaultReturnPolicyId"
            accepter={SelectPickerWithActions}
            data={state.data.returnPolicies}
            value={state.defaultsEdit.defaultReturnPolicyId ?? null}
            valueKey="id"
            labelKey="name"
            renderMenuItem={(label: ReactNode, item: any) => (
              <SelectOptionWithActions label={renderItem(label, item)}>
                <IconButton
                  size="xs"
                  icon={<EditIcon />}
                  appearance="subtle"
                  onClick={() => handleEditReturnPolicy(item as AllegroReturnPolicy)}
                />
                <IconButton
                  size="xs"
                  icon={<TrashIcon />}
                  appearance="subtle"
                  onClick={() => handleDeleteReturnPolicy(item as AllegroReturnPolicy)}
                />
              </SelectOptionWithActions>
            )}
            renderValue={renderItem}
            cleanable
          />
          <Button appearance="primary" onClick={() => handleEditReturnPolicy()}>
            Create new return terms
          </Button>
          <FilePicker
            accept=".csv,.xlsx,.xls"
            onPick={(file) => handleImportReturnPolicies(file)}
          >
            <Button appearance="primary">
              Import
            </Button>
          </FilePicker>
          <Button appearance="primary" onClick={() => handleExportReturnPolicies()}>
            Export
          </Button>
        </Stack>
      </Form.Group>
      <Form.Group>
        <Form.ControlLabel>Complaints terms</Form.ControlLabel>
        <Stack spacing={20}>
          <Form.Control
            name="defaultWarrantyId"
            accepter={SelectPickerWithActions}
            data={state.data.impliedWarranties}
            value={state.defaultsEdit.defaultWarrantyId ?? null}
            valueKey="id"
            labelKey="name"
            renderMenuItem={(label: ReactNode, item: any) => (
              <SelectOptionWithActions label={renderItem(label, item)}>
                <IconButton
                  size="xs"
                  icon={<EditIcon />}
                  appearance="subtle"
                  onClick={() => handleEditImpliedWarranty(item as AllegroImpliedWarranty)}
                />
              </SelectOptionWithActions>
            )}
            renderValue={renderItem}
            cleanable
          />
          <Button appearance="primary" onClick={() => handleEditImpliedWarranty()}>
            Create new complaints terms
          </Button>
          <FilePicker
            accept=".csv,.xlsx,.xls"
            onPick={(file) => handleImportImpliedWarranties(file)}
          >
            <Button appearance="primary">
              Import
            </Button>
          </FilePicker>
          <Button appearance="primary" onClick={() => handleExportImpliedWarranties()}>
            Export
          </Button>
        </Stack>
      </Form.Group>
      <Form.Group>
        <ButtonToolbar>
          <Button
            appearance="primary"
            onClick={() => onSave()}
            loading={state.isSaving}
            type="submit"
          >
            Save
          </Button>
          <Button
            appearance="ghost"
            onClick={() => onCancel()}
          >
            Cancel
          </Button>
        </ButtonToolbar>
      </Form.Group>
      <EshopSetupAllegroReturnPolicyEditModal
        isOpen={state.returnPolicies.isEditing}
        marketplaceCode={eshop.code}
        returnPolicy={state.returnPolicies.editingReturnPolicy}
        onClose={() => handleReturnPolicyClosed()}
        onSaved={() => handleReturnPolicySaved()}
      />
      <EshopSetupAllegroImpliedWarrantyEditModal
        isOpen={state.impliedWarranties.isEditing}
        marketplaceCode={eshop.code}
        impliedWarranty={state.impliedWarranties.editingImpliedWarranty}
        onClose={() => handleImpliedWarrantyClosed()}
        onSaved={() => handleImpliedWarrantySaved()}
      />
      {deletingReturnPolicy && (
        <Alert
          isOpen
          isLoading={state.returnPolicies.isDeleting}
          title="Confirmation"
          onConfirm={() => handleDeleteReturnPolicyConfirm(deletingReturnPolicy)}
          onClose={() => handleDeleteReturnPolicyClose()}
        >
          <p>
            Do you really want to
            {' '}
            <strong>delete</strong>
            {' '}
            return terms
            {' '}
            <strong>{deletingReturnPolicy.name}</strong>
            ?
          </p>
        </Alert>
      )}
    </Form>
  );
}
